import { apiBaseUrl, apiBaseSocket } from './config.js';
import { initializeSimulationTimer } from './simulationTimer.js';
import { showMementoMoriModal } from './mementoMori.js';
import { showStorySimulationModal } from './storySimulation.js';
import { t, confirmT, updateElementText } from './translationHelper.js';
import translations, { getCompanyI18n } from './translations.js';
globalThis.ZTranslations = translations;

// Optimized HLS configuration - reduced buffer for faster loading
const optimizedHlsConfig = {
  startLevel: -1,  // Auto quality selection
  capLevelToPlayerSize: true,
  maxBufferLength: 10,  // Reduced from 60
  maxMaxBufferLength: 15,  // Reduced from 60
  maxBufferSize: 5 * 1000 * 1000,  // Reduced from 30MB to 5MB
  maxBufferHole: 0.5,
  enableWorker: true,
  lowLatencyMode: false,
  backBufferLength: 5  // Keep only 5s of back buffer
};

// Login banner rotation logic
const loginBannerVideo = document.getElementById('LoginBanner');
const bannerSequence = [2, 3, 2, 4];
let currentSequenceIndex = 0;
let loginHlsInstance = null;
let loginBannerLoaded = false;

function loadLoginBanner(bannerNumber) {
  const bannerUrl = `${apiBaseUrl}/uploads/banners/index/${bannerNumber}/playlist.m3u8`;
  const loginBannerContainer = document.getElementById('login-banner');

  // Clean up existing instance
  if (loginHlsInstance) {
	loginHlsInstance.destroy();
	loginHlsInstance = null;
  }

  if (Hls.isSupported()) {
	loginHlsInstance = new Hls(optimizedHlsConfig);
	loginHlsInstance.loadSource(bannerUrl);
	loginHlsInstance.attachMedia(loginBannerVideo);
	loginHlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
	  loginBannerVideo.play().catch(err => console.warn('Login banner play failed:', err));
	  loginBannerLoaded = true;
	  if (loginBannerContainer) loginBannerContainer.classList.add('banner-loaded');
	});
  } else if (loginBannerVideo.canPlayType('application/vnd.apple.mpegurl')) {
	loginBannerVideo.src = bannerUrl;
	loginBannerVideo.addEventListener('loadedmetadata', () => {
	  loginBannerVideo.play().catch(err => console.warn('Login banner play failed:', err));
	  loginBannerLoaded = true;
	  if (loginBannerContainer) loginBannerContainer.classList.add('banner-loaded');
	}, { once: true });
  }
}

function rotateLoginBanner() {
  const bannerNumber = bannerSequence[currentSequenceIndex];
  loadLoginBanner(bannerNumber);
  currentSequenceIndex = (currentSequenceIndex + 1) % bannerSequence.length;
}

// Start rotation
rotateLoginBanner();
setInterval(rotateLoginBanner, 5000); // 5 seconds each

export async function login(portfolioId, password) {
	const preferredLanguage = localStorage.getItem('preferredLanguage') || 'en';
  try {
	const response = await fetch(`${apiBaseUrl}/api/authenticate`, {
	  method: 'POST',
	  headers: { 'Content-Type': 'application/json' },
	  body: JSON.stringify({ portfolioId, password, preferredLanguage }),
	  credentials: 'include',
	});

	if (response.ok) {
	  const data = await response.json();

	 if (data.firstLogin) {
	   // Persist auth first
	   localStorage.setItem('portfolioId', portfolioId);
	   localStorage.setItem('token', data.token);
	   localStorage.setItem('role', data.role);
	   if (data.sessionId) {
		 localStorage.setItem('sessionId', data.sessionId);
	   }
	 
	   // Use a stateful flag instead of a boolean trap
	   localStorage.setItem('firstLoginStatus', 'pending');
	 
	   // Ensure storage is flushed before we rely on it elsewhere
	   await ensureStorageReady(['portfolioId', 'token']);
	 
	   // Start first-login flow
	   showFirstLoginModal(portfolioId);
	 } else {
		// Store normal login details
		localStorage.setItem('portfolioId', portfolioId);
		localStorage.setItem('token', data.token);
		localStorage.setItem('role', data.role);
		if (data.sessionId) {
		  localStorage.setItem('sessionId', data.sessionId);
		} else {
		  localStorage.removeItem('sessionId');
		}

		// Redirect based on role
		if (data.role === 'broker') {
		  window.location.href = 'order.html';
		} else if (data.role === 'superuser') {
		  window.location.href = 'boss.html';
		} else {
		  window.location.href = 'zfloor.html';
		}
	  }
	} else {
	  alert(t('auth.login_failed_message', 'Login failed. Please check your credentials and try again.'));
	}
  } catch (error) {
	console.error('Error during login:', error);
	alert(t('auth.login_error_message', 'An error occurred during login. Please try again later.'));
  }
}

async function ensureStorageReady(keys) {
	const maxAttempts = 15;
	const delayMs = 100;

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		const missingKeys = keys.filter(key => {
			const value = localStorage.getItem(key);
			return !value || value === 'undefined' || value === 'null' || value.trim() === '';
		});

		if (missingKeys.length === 0) {
			return true; // Storage is ready
		}

		await new Promise(resolve => setTimeout(resolve, delayMs));
	}

	// Final check before throwing an error
	const finalMissingKeys = keys.filter(key => !localStorage.getItem(key));
	if (finalMissingKeys.length > 0) {
		console.error('Final check: Missing storage keys:', finalMissingKeys);
		throw new Error(`LocalStorage is still missing keys after multiple attempts: ${finalMissingKeys.join(', ')}`);
	}
}

window.claimCompany = async function () {
  try {
	if (!selectedCompanyId) {
	  window.alert(t('validation.select_company_first_message', 'Please select a company first before claiming.'));
	  return;
	}

	console.log(`Attempting to claim company: ${selectedCompanyId}`);

	// Find selected company in the DOM
	const selectedCompany = document.querySelector(`[data-id="${selectedCompanyId}"]`);
	if (!selectedCompany) {
	  throw new Error('Selected company not found.');
	}

	// Extract company details
	const companyName = selectedCompany.querySelector('.company-header h5')?.textContent || '';
	const companyDescription = selectedCompany.querySelector('.company-description')?.textContent || '';
	const companyProduct = selectedCompany.querySelector('.company-output')?.textContent.replace('Output:', '').trim() || '';

	console.log('Company Selected:', { companyName, companyDescription, companyProduct });

	// Fill the modal with company details
	const modalContainer = document.getElementById('firstLoginModal');
	if (!modalContainer) throw new Error('Modal container not found.');

	modalContainer.innerHTML = `
	  <div class="modal-content">
		<div class="section personal-info">
		  <input type="text" id="userName" placeholder="${t('auth.enter_trader_name_placeholder', 'Enter your Trader Name')}" maxlength="30" required>
		  <input type="password" id="newPassword" placeholder="${t('auth.enter_new_password_placeholder', 'Enter your new password')}" minlength="8">
		</div>
		<div class="section company-selection">
		  <h4>${t('marketing.tailor_stock_choice_message', 'Tailor your stock choice to align exactly with your values.')}</h4>
		  <label>${t('forms.company_name_fixed_label', 'Company Name (fixed):')}</label>
		  <input type="text" id="companyName" value="${companyName}" readonly>
		  <label>${t('forms.company_description_label', 'Company Description:')}</label>
		  <textarea id="companyDescription">${companyDescription}</textarea>
		  <label>${t('forms.company_product_label', 'Company Product/Output:')}</label>
		  <input type="text" id="companyProduct" value="${companyProduct}">
		  <button id="completeProfileBtn" class="setup-btn">${t('buttons.complete_setup_button', 'Complete Setup')}</button>
		</div>
	  </div>
	`;
  } catch (error) {
	console.error('Error selecting company:', error);
	window.alert(t('messages.error', `Failed to select company: ${error.message}`));
  }
};

/// Gate modal functions
function showFreestyleGate() {
  return new Promise((resolve, reject) => {
	const modal = document.getElementById('zexFreestyleIntro');
	if (!modal) return resolve();
	
	modal.setAttribute('aria-hidden', 'false');
	modal.style.display = 'flex';
	
	const onClick = (e) => {
	  const continueBtn = e.target.closest('[data-zex-action="continue"]');
	  const closeBtn = e.target.closest('[data-zex-action="close"]');
	  
	  if (continueBtn) {
		modal.removeEventListener('click', onClick);
		modal.setAttribute('aria-hidden', 'true');
		modal.style.display = 'none';
		resolve();
	  } else if (closeBtn) {
		modal.removeEventListener('click', onClick);
		modal.setAttribute('aria-hidden', 'true');
		modal.style.display = 'none';
		window.location.href = 'index.html';
		reject(); // Reject to prevent further execution
	  }
	};
	
	modal.addEventListener('click', onClick);
  });
}

function showMementoGate() {
  return new Promise((resolve, reject) => {
	const modal = document.getElementById('zexMementoIntro');
	if (!modal) return resolve();
	
	modal.setAttribute('aria-hidden', 'false');
	modal.style.display = 'flex';
	
	const onClick = (e) => {
	  const continueBtn = e.target.closest('[data-zex-action="continue"]');
	  const closeBtn = e.target.closest('[data-zex-action="close"]');
	  
	  if (continueBtn) {
		modal.removeEventListener('click', onClick);
		modal.setAttribute('aria-hidden', 'true');
		modal.style.display = 'none';
		resolve();
	  } else if (closeBtn) {
		modal.removeEventListener('click', onClick);
		modal.setAttribute('aria-hidden', 'true');
		modal.style.display = 'none';
		window.location.href = 'index.html';
		reject(); // Reject to prevent further execution
	  }
	};
	
	modal.addEventListener('click', onClick);
  });
}
// Simulator Key Validation Modal
function showSimulatorKeyModal(onSuccess, validationType = 'simulator') {
  console.log('showSimulatorKeyModal function started, validationType:', validationType);

  const modal = document.createElement('div');
  modal.classList.add('simulator-key-modal');
  modal.style.display = 'flex';

  const title = validationType === 'session'
    ? t('simulation.enter_key_title_session', 'Session Creation Access Required')
    : t('simulation.enter_key_title', 'Simulator Access Required');

  const description = validationType === 'session'
    ? t('simulation.enter_key_description_session', 'Please enter your access key to create a new session.')
    : t('simulation.enter_key_description', 'Please enter your simulator access key or scan QR code to continue.');

  modal.innerHTML = `
	<div class="simulator-key-modal-content">
	  <h2>${title}</h2>
	  <p>${description}</p>

	  <div class="key-input-section">
		<input
		  type="text"
		  id="simulatorKeyInput"
		  placeholder="${t('simulation.enter_key_placeholder', 'Enter simulator key...')}"
		  maxlength="50"
		/>
		<button id="validateKeyButton" class="simulator-setup-btn">${t('buttons.validate_key', 'Validate Key')}</button>
	  </div>

	  <div class="qr-scan-section">
		<p class="or-divider">${t('labels.or', 'OR')}</p>
		<button id="scanQRButton" class="simulator-secondary-btn">${t('buttons.scan_qr_code', 'Scan QR Code')}</button>
		<div id="qr-scanner-simulator" style="display: none; margin-top: 20px;"></div>
	  </div>

	  <button id="closeKeyModal" class="simulator-close-btn">${t('buttons.cancel', 'Cancel')}</button>
	</div>
  `;

  document.body.appendChild(modal);

  // Validate Key button handler
  document.getElementById('validateKeyButton').addEventListener('click', async () => {
	const code = document.getElementById('simulatorKeyInput').value.trim();

	if (!code) {
	  window.alert(t('validation.enter_key_message', 'Please enter a simulator pass code.'));
	  return;
	}

	try {
	  const url = `${apiBaseUrl}/api/simulator-passes/validate/${encodeURIComponent(code)}?incrementUsage=true`;
	  console.log('🔑 Validating pass with URL:', url);
	  const response = await fetch(url);

	  if (response.ok) {
		const data = await response.json();
		console.log('📊 Server response:', data);

		if (data.valid) {
		  // Check if pass allows the requested action
		  if (validationType === 'session' && !data.pass?.allowsSessionCreation) {
			window.alert(t('validation.session_creation_not_allowed', 'This pass does not allow session creation. Please use a different pass.'));
			return;
		  }

		  console.log('✅ Pass validated successfully. Remaining uses:', data.pass?.remainingUses);
		  modal.remove();
		  if (onSuccess) onSuccess();
		} else {
		  window.alert(t('validation.invalid_key_message', data.message || 'Invalid simulator pass. Please try again.'));
		}
	  } else {
		window.alert(t('validation.invalid_key_message', 'Invalid simulator pass. Please try again.'));
	  }
	} catch (error) {
	  console.error('Error validating pass:', error);
	  window.alert(t('messages.error', 'Error validating pass. Please try again.'));
	}
  });

  // Scan QR Code button handler
  let html5QrCode = null;
  document.getElementById('scanQRButton').addEventListener('click', () => {
	const scannerDiv = document.getElementById('qr-scanner-simulator');
	const scanButton = document.getElementById('scanQRButton');

	if (scannerDiv.style.display === 'none') {
	  // Start scanner
	  scannerDiv.style.display = 'block';
	  scanButton.textContent = t('buttons.stop_scanning', 'Stop Scanning');

	  if (typeof Html5Qrcode !== 'undefined') {
		html5QrCode = new Html5Qrcode("qr-scanner-simulator");

		html5QrCode.start(
		  { facingMode: "environment" },
		  { fps: 10, qrbox: { width: 250, height: 250 } },
		  async (decodedText) => {
			console.log('QR Code scanned:', decodedText);

			// Stop scanner
			html5QrCode.stop().then(() => {
			  scannerDiv.style.display = 'none';
			  scanButton.textContent = t('buttons.scan_qr_code', 'Scan QR Code');
			});

			// Validate the scanned code
			try {
			  // Extract code from QR URL if it's a full URL, otherwise use as-is
			  let code = decodedText;
			  try {
				const url = new URL(decodedText);
				const codeParam = url.searchParams.get('code');
				if (codeParam) code = codeParam;
			  } catch (e) {
				// Not a URL, use decodedText as-is
			  }

			  const validationUrl = `${apiBaseUrl}/api/simulator-passes/validate/${encodeURIComponent(code)}?incrementUsage=true`;
			  console.log('🔑 Validating QR scanned pass with URL:', validationUrl);
			  const response = await fetch(validationUrl);

			  if (response.ok) {
				const data = await response.json();
				console.log('📊 Server response:', data);

				if (data.valid) {
				  // Check if pass allows the requested action
				  if (validationType === 'session' && !data.pass?.allowsSessionCreation) {
					window.alert(t('validation.session_creation_not_allowed', 'This pass does not allow session creation. Please use a different pass.'));
					return;
				  }

				  console.log('✅ Pass validated successfully. Remaining uses:', data.pass?.remainingUses);
				  modal.remove();
				  if (onSuccess) onSuccess();
				} else {
				  window.alert(t('validation.invalid_key_message', data.message || 'Invalid simulator pass. Please try again.'));
				}
			  } else {
				window.alert(t('validation.invalid_key_message', 'Invalid simulator pass. Please try again.'));
			  }
			} catch (error) {
			  console.error('Error validating pass:', error);
			  window.alert(t('messages.error', 'Error validating pass. Please try again.'));
			}
		  }
		).catch((err) => {
		  console.error('Error starting QR scanner:', err);
		  window.alert(t('messages.error', 'Error starting camera. Please check permissions.'));
		  scannerDiv.style.display = 'none';
		  scanButton.textContent = t('buttons.scan_qr_code', 'Scan QR Code');
		});
	  } else {
		window.alert(t('messages.error', 'QR scanner not available. Please enter key manually.'));
		scannerDiv.style.display = 'none';
		scanButton.textContent = t('buttons.scan_qr_code', 'Scan QR Code');
	  }
	} else {
	  // Stop scanner
	  if (html5QrCode) {
		html5QrCode.stop().then(() => {
		  scannerDiv.style.display = 'none';
		  scanButton.textContent = t('buttons.scan_qr_code', 'Scan QR Code');
		});
	  }
	}
  });

  // Close button handler
  document.getElementById('closeKeyModal').addEventListener('click', () => {
	if (html5QrCode) {
	  html5QrCode.stop().catch(() => {});
	}
	modal.remove();
  });
}

export function showSimulatorChoiceModal() {
  console.log('showSimulatorChoiceModal function started');

  const modal = document.createElement('div');
  modal.classList.add('simulation-modal');
  
  modal.innerHTML = `
	<div class="simulation-modal-content">
	  <h2>${t('modals.choose_simulation_title', 'Choose Your Simulation')}</h2>

	  <!-- Banner Buttons -->
	  <div class="simulation-banners">
		<video id="FreeFormBanner" autoplay muted loop playsinline class="simulation-banner-button simulation-freeform-banner">
		</video>

		<video id="MementoMoriBanner" autoplay muted loop playsinline class="simulation-banner-button simulation-memento-banner">
		</video>

		<video id="StorySimulationBanner" autoplay muted loop playsinline class="simulation-banner-button simulation-story-banner">
		</video>
	  </div>

	  <div style="padding: 0 30px 30px 30px;">
		<button id="closeChoiceModal" class="simulation-secondary-btn">${t('buttons.cancel_button', 'Cancel')}</button>
	  </div>
	</div>
  `;
  
  document.body.appendChild(modal);
  console.log('Modal added to body');

  // Load videos with HLS support following your existing pattern
  const freeFormVideo = document.getElementById('FreeFormBanner');
  const mementoVideo = document.getElementById('MementoMoriBanner');
  const storySimVideo = document.getElementById('StorySimulationBanner');

  // Track video ready states
  let freeFormReady = false;
  let mementoReady = false;
  let storySimReady = false;
  let freeFormHls, mementoHls, storySimHls;

  // Fast-loading video function with immediate clickability - now using optimized config
  function loadVideoWithFallback(video, bannerUrl, onReady) {
	// Enable click immediately - don't wait for full load
	setTimeout(() => onReady(), 100);

	if (typeof Hls !== 'undefined' && Hls.isSupported()) {
	const hls = new Hls(optimizedHlsConfig);
	  hls.loadSource(bannerUrl);
	  hls.attachMedia(video);

	  hls.on(Hls.Events.MANIFEST_PARSED, () => {
		video.play().catch(err => console.warn('Video play failed:', err));
	  });
	  
	  // Enable interaction as soon as first fragment loads
	  hls.on(Hls.Events.FRAG_LOADED, () => {
		onReady();
	  });
	  
	  hls.on(Hls.Events.ERROR, (event, data) => {
		if (data.fatal) {
		  console.warn('HLS failed, trying direct play');
		  hls.destroy();
		  video.src = bannerUrl;
		  video.play().catch(err => console.warn('Direct play failed:', err));
		  onReady();
		}
	  });
	  
	  return hls;
	} else if (video.canPlayType('application/vnd.apple.mpegurl')) {
	  video.src = bannerUrl;
	  video.addEventListener('loadstart', () => onReady());
	  video.addEventListener('loadedmetadata', () => {
		video.play().catch(err => console.warn('Video play failed:', err));
	  });
	  return null;
	}
  }

  // Load Free Form video
  if (freeFormVideo) {
	const bannerUrl = `${apiBaseUrl}/uploads/banners/simulator/1/playlist.m3u8`;
	freeFormHls = loadVideoWithFallback(freeFormVideo, bannerUrl, () => {
	  freeFormReady = true;
	  console.log('Free Form video ready for interaction');
	});
  }

  // Load Memento Mori video
  if (mementoVideo) {
	const bannerUrl = `${apiBaseUrl}/uploads/banners/simulator/2/playlist.m3u8`;
	mementoHls = loadVideoWithFallback(mementoVideo, bannerUrl, () => {
	  mementoReady = true;
	  console.log('Memento Mori video ready for interaction');
	});
  }

  // Load Story Simulation video (temporarily using FreeForm banner until dedicated banner is created)
  if (storySimVideo) {
	const bannerUrl = `${apiBaseUrl}/uploads/banners/simulator/3/playlist.m3u8`;
	storySimHls = loadVideoWithFallback(storySimVideo, bannerUrl, () => {
	  storySimReady = true;
	  console.log('Story Simulation video ready for interaction');
	});
  }

// Event listeners - Video banner clicks with KEY VALIDATION FIRST
 freeFormVideo.addEventListener('click', async (e) => {
   console.log('Free Form banner clicked');
   e.preventDefault();
   e.stopPropagation();
   modal.remove();

   // Show key validation modal first
   showSimulatorKeyModal(async () => {
	 try {
	   // After key validation, show gate modal
	   await showFreestyleGate();
	   // Then show questionnaire
	   showSimulatorQuestionModal();
	 } catch (error) {
	   // User clicked close, redirect handled in gate function
	   console.log('User cancelled at gate modal');
	 }
   });
 });

 mementoVideo.addEventListener('click', async (e) => {
   console.log('Memento Mori banner clicked');
   e.preventDefault();
   e.stopPropagation();
   modal.remove();

   // Show key validation modal first
   showSimulatorKeyModal(async () => {
	 try {
	   // After key validation, show gate modal
	   await showMementoGate();
	   // Then show memento questions
	   showMementoMoriModal();
	 } catch (error) {
	   // User clicked close, redirect handled in gate function
	   console.log('User cancelled at gate modal');
	 }
   });
 });

 storySimVideo.addEventListener('click', (e) => {
   console.log('Story Simulation banner clicked');
   e.preventDefault();
   e.stopPropagation();
   modal.remove();

   // Show key validation modal first
   showSimulatorKeyModal(() => {
	 // After key validation, directly show story theme selection
	 showStorySimulationModal();
   });
 });

  document.getElementById('closeChoiceModal').addEventListener('click', () => {
	modal.remove();
  });
}


function showChoiceModal() {
	const modal = document.getElementById('choiceModal');
	modal.style.display = 'block'; // Show the modal

	// Handle button clicks
	document.getElementById('scanButton').onclick = function () {
		modal.style.display = 'none';
		window.location.href = 'collect.html'; // Redirect to scan items page
	};

	document.getElementById('fulfillButton').onclick = function () {
		modal.style.display = 'none';
		window.location.href = 'order.html'; // Redirect to fulfill orders page
	};
}

export function showFirstLoginModal(portfolioId) {
  console.log('showFirstLoginModal called with portfolioId:', portfolioId);

  const modalHTML = `
	<div id="firstLoginModal" class="modal">
	  <div class="modal-content">
		<h2 class="zeconComp">Welcome!<br> Let's set up your trading portfolio.</h2>
		<div id="setupContainer"></div>
	  </div>
	</div>
  `;
  document.body.insertAdjacentHTML('beforeend', modalHTML);

  // Start with step 1
  showSetupStep(1, portfolioId);
}

function showSetupStep(step, portfolioId) {
  const container = document.getElementById('setupContainer');
  
  if (step === 1) {
	// Step 1: Trader Name
	container.innerHTML = `
	  <div class="setup-step">
		<h3>Step 1: Choose Your Trader Name</h3>
		<div class="input-group">
		  <input
			type="text"
			id="userName"
			placeholder="${t('auth.enter_trader_name_placeholder', 'Enter your Trader Name')}"
			maxlength="30"
			required
		  >
		</div>
		<div class="firstLogin-button-group">
		  <button id="nextStepBtn" class="firstLogin-primary-btn">${t('buttons.next_button', 'Next')}</button>
		  <button id="cancelBtn" class="firstLogin-secondary-btn">${t('buttons.cancel_button', 'Cancel')}</button>
		</div>

	  </div>
	`;
	
	document.getElementById('cancelBtn').addEventListener('click', () => {
	  // Clear first-login flag so modal doesn't reopen
	  localStorage.removeItem('firstLoginStatus'); // clear the stateful flag
	  localStorage.removeItem('setupUserName');
	  localStorage.removeItem('setupPassword');
	  localStorage.removeItem('llmResponseQ1');
	  localStorage.removeItem('llmResponseQ2');
	  localStorage.removeItem('llmResponseQ3');
	  
	  // Optional: also clear auth to force a clean login after cancel
	  localStorage.removeItem('portfolioId');
	  localStorage.removeItem('token');
	  localStorage.removeItem('role');
	  localStorage.removeItem('sessionId');
	  
	  const m = document.getElementById('firstLoginModal'); 
	  if (m) m.remove();
	  window.location.href = 'index.html';
	});

	document.getElementById('nextStepBtn').addEventListener('click', () => {
	  const name = document.getElementById('userName').value.trim();
	  if (!name) {
		window.alert(t('validation.fill_trader_name_message', 'Please enter your trader name.'));
		return;
	  }
	  localStorage.setItem('setupUserName', name);
	  showSetupStep(2, portfolioId);
	});
	
  } else if (step === 2) {
	// Step 2: Password
	container.innerHTML = `
	  <div class="setup-step">
		<h3>Step 2: Set Your Password</h3>
		<div class="input-group">
		  <input
			type="password"
			id="newPassword"
			placeholder="${t('auth.enter_new_password_placeholder', 'Enter your new password')}"
			minlength="8"
		  >
		</div>
		<div class="firstLogin-button-group">
		<button id="nextStepBtn" class="firstLogin-primary-btn">${t('buttons.next_button', 'Next')}</button>
		<button id="cancelBtn" class="firstLogin-secondary-btn">${t('buttons.cancel_button', 'Cancel')}</button>
		</div>

	  </div>
	`;
	
	document.getElementById('cancelBtn').addEventListener('click', () => {
	  // Clear first-login flag so modal doesn't reopen
	  localStorage.removeItem('firstLoginStatus'); // clear the stateful flag
	  localStorage.removeItem('setupUserName');
	  localStorage.removeItem('setupPassword');
	  localStorage.removeItem('llmResponseQ1');
	  localStorage.removeItem('llmResponseQ2');
	  localStorage.removeItem('llmResponseQ3');
	  
	  // Optional: also clear auth to force a clean login after cancel
	  localStorage.removeItem('portfolioId');
	  localStorage.removeItem('token');
	  localStorage.removeItem('role');
	  localStorage.removeItem('sessionId');
	  
	  const m = document.getElementById('firstLoginModal'); 
	  if (m) m.remove();
	  window.location.href = 'index.html';
	});

	document.getElementById('nextStepBtn').addEventListener('click', () => {
	  const password = document.getElementById('newPassword').value;
	  if (!password || password.length < 8) {
		window.alert(t('validation.password_required_message', 'Password must be at least 8 characters.'));
		return;
	  }
	  localStorage.setItem('setupPassword', password);
	  showSetupStep(3, portfolioId);
	});
	
  } else if (step === 3) {
	// Step 3: Life Profits Questions
	startLLMConversationFlow(portfolioId);
  }
}

function startLLMConversationFlow(portfolioId) {
  showConversationStep(1, portfolioId);
}

async function showConversationStep(step, portfolioId) {
  const container = document.getElementById('setupContainer');
  
  const placeholders = [
	"I value playing with my kids...",
	"Depression seems to be sought after...",
	"I love love and everything about it...",
	"A drink with friends is worth everything...",
	"I feel rejection creates value...",
	"I see joy as the real currency of life...",
	"Helping others makes my life richer...",
	"Loneliness has become a very common experience..."
  ];
  
  const placeholdersQ3 = [
	"Dancing to techno music makes me forget my worries...",
	"Hanging with friends makes time fly...",
	"I miss my mom, and it reminds me what matters...",
	"Running regularly clears my head...",
	"Watching old cartoons makes me feel like a kid again...",
	"Getting a haircut recharges my batteries...",
	"Laughing makes life worth living..."
  ];

  function getRandomPlaceholder(pool = placeholders) {
	return pool[Math.floor(Math.random() * pool.length)];
  }

  if (step === 1) {
	container.innerHTML = `
	  <div class="conversation-step">
		<p><br><strong><u>Life Profits:</u></strong></p>
		<p><strong>1.)</strong> The value of consciously lived life. (Quality / Quantity / Positive or Negative)<br></p>
		<p><strong>1.1)</strong> The basic unit of value for the ZEconomy<br></p>
		<p><strong>2.)</strong> The use of value to articulate doing life in the now.</p><br>
		<p>What part of life, positive or negative, do you feel creates Life Profits?</p> 
		<input type="text" id="responseQ1" placeholder="${getRandomPlaceholder()}" maxlength="150"/>
		<div class="button-group">
		  <button id="cancelBtn" class="secondary-btn">${t('buttons.cancel_button', 'Cancel')}</button>
		  <button id="nextStepBtn" class="primary-btn">${t('buttons.next_button', 'Next')}</button>
		</div>
	  </div>
	`;
	
	const inputField = document.getElementById('responseQ1');
	inputField.placeholder = getRandomPlaceholder();
	setInterval(() => {
	  if (!inputField.value) {
		inputField.placeholder = getRandomPlaceholder();
	  }
	}, 3000);
	
  } else if (step === 2) {
	container.innerHTML = `
	  <div class="conversation-step">
		<p>In a single word, how would you express the output produced by this on life?</p>
		<input type="text" id="responseQ2" placeholder="(e.g., fun, wisdom, depression)" oninput="this.value = this.value.replace(/\\s/g, '').slice(0, 20)"/>
		<div class="firstLogin-button-group">
		  <button id="nextStepBtn" class="firstLogin-primary-btn">${t('buttons.next_button', 'Next')}</button>
		  <button id="cancelBtn" class="firstLogin-secondary-btn">${t('buttons.cancel_button', 'Cancel')}</button>
		</div>
	  </div>
	`;
	
  } else if (step === 3) {
	container.innerHTML = `
	  <div class="conversation-step">
		<p>How is this value created in life? What experiences, actions, or circumstances contribute to creating it?</p>
		<input type="text" id="responseQ3" placeholder="${getRandomPlaceholder(placeholdersQ3)}" maxlength="200"/>
		<div class="firstLogin-button-group">
		  <button id="nextStepBtn" class="firstLogin-primary-btn">${t('buttons.next_button', 'Next')}</button>
		  <button id="cancelBtn" class="firstLogin-secondary-btn">${t('buttons.cancel_button', 'Cancel')}</button>
		</div>
	  </div>
	`;
	
	const inputField = document.getElementById('responseQ3');
	inputField.placeholder = getRandomPlaceholder(placeholdersQ3);
	setInterval(() => {
	  if (!inputField.value) {
		inputField.placeholder = getRandomPlaceholder(placeholdersQ3);
	  }
	}, 3000);
	
  } else if (step === 4) {
	container.innerHTML = `<p>DO NOT LEAVE PAGE! <br> Generating ZEconomy Company suggestions...</p>`;
	
	const responseQ1 = localStorage.getItem('llmResponseQ1') || '';
	const responseQ2 = localStorage.getItem('llmResponseQ2') || '';
	const responseQ3 = localStorage.getItem('llmResponseQ3') || '';
	
	await ensureStorageReady(['token','portfolioId']);
	
	fetch(`${apiBaseUrl}/api/company/generate-company-profile`, {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json',
		'Authorization': `Bearer ${localStorage.getItem('token')}`
	  },
	  credentials: 'include', 
	  body: JSON.stringify({ responseQ1, responseQ2, responseQ3 })
	})

	.then(response => response.json())
	.then(data => {
	  const companies = data.companies || [];
	  const empathyMessage = data.empathy || '';
	  
	  if (!Array.isArray(companies) || companies.length === 0) {
		container.innerHTML = `
		  <p>No matching companies found for your responses.</p>
		  <button id="cancelBtn" class="secondary-btn">${t('buttons.cancel_button', 'Return to Login')}</button>
		`;
		document.getElementById('cancelBtn').addEventListener('click', () => {
		  document.getElementById('firstLoginModal').remove();
		  window.location.href = 'index.html';
		});
		return;
	  }
	  
	  let html = empathyMessage ? `<p><em>${empathyMessage}</em></p>` : '';
	  html += `<p>We found ${companies.length} matching companies. Select one to complete setup:</p>`;
	  
	  const outputLabel = t('labels.output', 'Output');
	  const selectLabel = t('buttons.select', 'Select');
	  
	  companies.forEach((company, index) => {
		const { getCompanyI18n } = window.ZTranslations || {};
		const pickI18n = (typeof getCompanyI18n === 'function')
		  ? getCompanyI18n
		  : (c, tFn) => {
			  const rowId = (c.csvRow ?? c.Id ?? c.ID ?? '').toString().trim();
			  const key = (k) => rowId ? `companies_by_id.${rowId}.${k}` : '';
			  const name = t(key('Company_Name'), c['Company Name'] || c.name || '');
			  const out  = t(key('Out_put'),       c['Out put'] || c['Output'] || c.output || '');
			  const desc = t(key('Description'),   c['Description'] || c.description || '');
			  return { name, out, desc, rowId };
			};
		
		const { name, out, desc, rowId } = pickI18n(company, t);
		
		html += `
		  <div class="company-suggestion" data-index="${index}">
			<h5>${name || (company.name || company["Company Name"] || 'Untitled')}</h5>
			<p><strong>${outputLabel}:</strong> ${out || (company.output || company["Out put"] || '')}</p>
			<p>${desc || (company.description || company["Description"] || '')}</p>
			${rowId ? `<div class="i18n-id" style="font-size:.85em;opacity:.6">ID : ${rowId}</div>` : ''}
			<button class="claim-company-btn" data-index="${index}">${selectLabel}</button>
		  </div>
		`;
	  });
	  
	  html += `<button id="cancelBtn" class="secondary-btn" style="margin-top: 20px;">${t('buttons.cancel_button', 'Cancel')}</button>`;
	  
	  container.innerHTML = html;
	  
	  document.getElementById('cancelBtn').addEventListener('click', () => {
		document.getElementById('firstLoginModal').remove();
		window.location.href = 'index.html';
	  });
	  
	  const claimButtons = container.querySelectorAll('.claim-company-btn');
	  claimButtons.forEach(btn => {
		btn.addEventListener('click', async (evt) => {
		  const idx = evt.target.getAttribute('data-index');
		  const selectedCompany = companies[idx];
		  await completeSetup(portfolioId, selectedCompany);
		});
	  });
	})
	.catch(error => {
	  container.innerHTML = `
		<p>Error generating company suggestions. Please try again.</p>
		<button id="cancelBtn" class="secondary-btn">${t('buttons.cancel_button', 'Return to Login')}</button>
	  `;
	  document.getElementById('cancelBtn').addEventListener('click', () => {
		document.getElementById('firstLoginModal').remove();
		window.location.href = 'index.html';
	  });
	  console.error(error);
	});
	
	return;
  }
  
  // Add cancel button listener for all conversation steps
  const cancelBtn = document.getElementById('cancelBtn');
  if (cancelBtn) {
	cancelBtn.addEventListener('click', () => {
	  // Clear first-login flag so modal doesn't reopen
	  localStorage.removeItem('firstLoginStatus'); // clear the stateful flag
	  localStorage.removeItem('setupUserName');
	  localStorage.removeItem('setupPassword');
	  localStorage.removeItem('llmResponseQ1');
	  localStorage.removeItem('llmResponseQ2');
	  localStorage.removeItem('llmResponseQ3');
	  
	  // Optional: also clear auth to force a clean login after cancel
	  localStorage.removeItem('portfolioId');
	  localStorage.removeItem('token');
	  localStorage.removeItem('role');
	  localStorage.removeItem('sessionId');
	  
	  const m = document.getElementById('firstLoginModal'); 
	  if (m) m.remove();
	  window.location.href = 'index.html';
	});
  }
  
  // Add next button listener for steps 1-3
  const nextStepBtn = document.getElementById('nextStepBtn');
  if (nextStepBtn) {
	nextStepBtn.addEventListener('click', () => {
	  const inputField = container.querySelector('input');
	  const answer = inputField.value.trim();
	  if (!answer) {
		window.alert(t('validation.fill_response_message', "Please enter your response."));
		return;
	  }
	  localStorage.setItem(`llmResponseQ${step}`, answer);
	  showConversationStep(step + 1, portfolioId);
	});
  }
}

async function completeSetup(portfolioId, selectedCompany) {
  try {
	const name = localStorage.getItem('setupUserName');
	const newPassword = localStorage.getItem('setupPassword');
	const token = localStorage.getItem('token');
	const sessionId = localStorage.getItem('sessionId');
	
	if (!name || !newPassword) {
	  window.alert(t('validation.setup_incomplete', 'Setup information missing. Please start over.'));
	  return;
	}
	
	const companyName = selectedCompany['Company Name'] || selectedCompany.name || '';
	const companyDescription = selectedCompany['Description'] || selectedCompany.description || '';
	const companyOutput = selectedCompany['Out put'] || selectedCompany.output || '';
	const systemCompanyId = selectedCompany._id || selectedCompany.id || '';
	const csvId = selectedCompany.csvRow || selectedCompany.Id || selectedCompany.ID || '';
	
	console.log('Completing setup with:', { 
	  portfolioId, 
	  name, 
	  companyName, 
	  systemCompanyId,
	  csvId 
	});
	
	const preferredLanguage = localStorage.getItem('preferredLanguage') || 'en';
	
	const response = await fetch(`${apiBaseUrl}/api/first-login`, {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json',
		'Authorization': `Bearer ${token}`
	  },
	  credentials: 'include',
	  body: JSON.stringify({
		portfolioId,
		name,
		newPassword,
		companyName,
		companyDescription,
		companyProduct: companyOutput,
		systemCompanyId,
		csvId,
		preferredLanguage
	  })
	});
	
	if (!response.ok) {
	  const error = await response.json();
	  throw new Error(error.error || 'Failed to complete setup');
	}
	
	const result = await response.json();
	console.log('Setup complete:', result);

	// Persist any new auth and mark first-login done
	if (result.token) localStorage.setItem('token', result.token);
	if (result.portfolioId) localStorage.setItem('portfolioId', result.portfolioId);
	if (result.sessionId) localStorage.setItem('sessionId', result.sessionId);
	if (result.role) localStorage.setItem('role', result.role);
	
	// Clean up temp storage and state
	localStorage.setItem('firstLoginStatus', 'done');
	localStorage.removeItem('setupUserName');
	localStorage.removeItem('setupPassword');
	localStorage.removeItem('llmResponseQ1');
	localStorage.removeItem('llmResponseQ2');
	localStorage.removeItem('llmResponseQ3');
	
	const modal = document.getElementById('firstLoginModal');
	if (modal) modal.remove();

	window.location.href = 'zfloor.html';

	
  } catch (error) {
	console.error('Error completing setup:', error);
	window.alert(t('messages.error', `Setup failed: ${error.message}`));
  }
}

function encodeData(data) {
  return btoa(unescape(encodeURIComponent(data))); // Simple Base64 encoding
}

function showFinalForm(company, hash) {
  const conversationContainer = document.getElementById('companyConversation');
  const rowId = (company.csvRow ?? company.Id ?? company.ID ?? '').toString().trim();

  const companyName = rowId
	? t(`companies_by_id.${rowId}.Company_Name`, company["Company Name"] || company.name || "Untitled")
	: (company["Company Name"] || company.name || "Untitled");

  const companyDescription = rowId
	? t(`companies_by_id.${rowId}.Description`, company["Description"] || company.description || "")
	: (company["Description"] || company.description || "");

  const companyOutput = rowId
	? t(`companies_by_id.${rowId}.Out_put`, company["Out put"] || company.output || "")
	: (company["Out put"] || company.output || "");

  conversationContainer.innerHTML = `
	<div class="company-final-form">
	  <h2 class="zeconComp">${t('review.title','Vérifiez les détails de la valeur de votre entreprise')}</h2>

	  <label>${t('review.company_name','Nom de l’entreprise')} :</label>
	  <span id="companyName">${companyName}<br><br></span>
	  <input type="hidden" name="companyName" value="${companyName}" />

	  <label>${t('review.company_description','Description de l’entreprise')} :</label>
	  <span id="companyDescription">${companyDescription}<br><br></span>
	  <input type="hidden" name="companyDescription" value="${companyDescription}" />

	  <label>${t('review.company_output','Produit / Production')} :</label>
	  <span id="companyProduct">${companyOutput}<br><br></span>
	  <input type="hidden" name="companyProduct" value="${companyOutput}" />

	  <button id="completeProfileBtn" class="setup-btn">${t('buttons.complete_setup_button','Terminer la configuration')}</button>
	</div>
  `;
}


async function generateDraftCompanyProfile() {
  const responseQ1 = localStorage.getItem('llmResponseQ1') || '';
  const responseQ2 = localStorage.getItem('llmResponseQ2') || '';
  const responseQ3 = localStorage.getItem('llmResponseQ3') || '';

  try {
	await ensureStorageReady(['token','portfolioId']);
	
	const apiResponse = await fetch(`${apiBaseUrl}/api/company/generate-company-profile`, {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json',
		'Authorization': `Bearer ${localStorage.getItem('token')}`
	  },
	  credentials: 'include',
	  body: JSON.stringify({ responseQ1, responseQ2, responseQ3 })
	});

	if (!apiResponse.ok) {
	  throw new Error(`HTTP error! status: ${apiResponse.status}`);
	}

	const result = await apiResponse.json();
	console.log('API Response:', result); // Debug log
	
	// Return just the companies array, not the whole response!
	return result.companies || [];
	
  } catch (error) {
	console.error("Error generating company profile:", error);
	throw error;
  }
}

// This function is called when the user submits the search form.
async function searchCompanies(query) {
  const searchResults = document.getElementById('searchResults');
  if (!query) {
	searchResults.innerHTML = '';
	searchResults.style.display = 'none';
	return;
  }

  try {
	// Call your backend search API
	const response = await fetch(`${apiBaseUrl}/api/companies/search?q=${encodeURIComponent(query)}`, {
	  method: 'GET',
	  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
	});

	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

	const companies = await response.json();
	console.log("✅ Companies received from API:", companies);

	if (companies.length === 0) {
	  searchResults.innerHTML = `
		<div class="no-results">
		  <p>No matching companies found</p>
		  <p class="suggestion-text">Try creating a new company instead</p>
		</div>
	  `;
	  return;
	}

	// Render results and add selection logic (localized via translations.js using csvRow)
	searchResults.innerHTML = companies
	  .map(company => {
		const rowId = company.csvRow; // provided by backend
		const name = t(rowId ? `companies_by_id.${rowId}.Company_Name` : '', company['Company Name'] || '');
		const out  = t(rowId ? `companies_by_id.${rowId}.Out_put`       : '', company['Out put'] || company['Output'] || '');
		const desc = t(rowId ? `companies_by_id.${rowId}.Description`   : '', company['Description'] || '');
		const risk = t(rowId ? `companies_by_id.${rowId}.Risk`          : '', company['Risk'] || '');

		const matchLabel  = t('labels.match',  'Match');
		const outputLabel = t('labels.output', 'Output');
		const riskLabel   = t('labels.risk',   'Risk');

		const relevanceScore = Math.min(100, Math.round(((company.relevanceScore ?? company.score ?? 0) * 5)));

		return `
		  <div class="company-result" data-id="${company._id}" onclick="selectCompany('${company._id}')">
			<div class="company-header">
			  <h5>${name}</h5>
			  <span class="company-symbol">${company['Symbol'] ?? ''}</span>
			  <span class="relevance-score">${matchLabel}: ${relevanceScore}%</span>
			</div>
			<div class="company-main">
			  <p class="company-output"><strong>${outputLabel}:</strong> ${out}</p>
			  <p class="company-description">${desc}</p>
			</div>
			<div class="company-details">
			  <p class="company-risk"><strong>${riskLabel}:</strong> ${risk}</p>
			</div>
		  </div>
		`;
	  })
	  .join('');

	searchResults.style.display = 'block';
  } catch (error) {
	console.error('Error searching companies:', error);
	searchResults.innerHTML = '<div class="error">Error searching companies. Please try again.</div>';
  }
}

let selectedCompanyId = null;

function selectCompany(companyId) {
  console.log(`Company selected: ${companyId}`);

  // Remove previous selection
  document.querySelectorAll('.company-result').forEach(el => el.classList.remove('selected-company'));

  // Highlight new selection
  const selectedCompany = document.querySelector(`[data-id="${companyId}"]`);
  if (selectedCompany) {
	selectedCompany.classList.add('selected-company');
	selectedCompanyId = companyId;
  }
}


function highlightMatch(text, query) {
	const regex = new RegExp(`(${query})`, 'gi');
	return text.replace(regex, '<mark>$1</mark>');
}

window.showInfo = function (message) {
	document.getElementById('infoText').innerHTML = message;
	document.getElementById('infoModal').style.display = 'flex';
};

export function openCreateSessionModal() {
	const modalHTML = `
		<div id="createSessionModal" class="modal">
			<div class="modal-content">
				<h2>${t('modals.create_new_session_title', 'Create New Session')}</h2>
				<p class="modal-info">
					${t('modals.session_creation_description', 'Create a new trading session as the Zederal Reserve Central Bank. You\'ll be able to manage and regulate the market.')}
				</p>
				
				<div class="input-group">
					<label for="sessionName">${t('forms.session_name_label', 'Session Name')}</label>
					<input
						type="text"
						id="sessionName"
						placeholder="${t('forms.session_name_placeholder', 'Enter session name (letters, numbers, underscores only)')}"
						pattern="[a-zA-Z0-9_]+"
						required
					>
				</div>

				<div class="input-group">
					<label for="superUserPassword">${t('auth.set_password_label', 'Set Password')}</label>
					<input
						type="password"
						id="superUserPassword"
						placeholder="${t('auth.secure_password_placeholder', 'Enter secure password')}"
						required
					>
				</div>

				<button id="submitSessionButton" class="primary-btn">${t('buttons.create_session_button', 'Create Session')}</button>
				<button id="closeModalButton" class="secondary-btn">${t('buttons.cancel_button', 'Cancel')}</button>
			</div>
		</div>

		<div id="credentialsModal" class="modal" style="display: none;">
			<div class="modal-content">
				<h2>Save Your Credentials</h2>
				<p class="warning"> Save this information now - it won't be shown again!</p>
				
				<div class="credentials-box">
					<div class="credential-item">
						<label>Portfolio ID:</label>
						<span id="generatedUsername"></span>
						<button class="copy-btn" data-copy="username">Copy</button>
					</div>
					<div class="credential-item">
						<label>Password:</label>
						<span id="generatedPassword"></span>
						<button class="copy-btn" data-copy="password">Copy</button>
					</div>
				</div>

				<div id="qrCodeContainer"></div>
				<button id="downloadQRCode" class="secondary-btn">Download QR Code</button>
				<button id="continueToSession" class="primary-btn">Go to Login</button>
			</div>
		</div>
	`;

	document.body.insertAdjacentHTML('beforeend', modalHTML);
	const modal = document.getElementById('createSessionModal');
	modal.style.display = 'block';

	// Handle session creation
	document.getElementById('submitSessionButton').addEventListener('click', async () => {
		const sessionNameInput = document.getElementById('sessionName');
		const passwordInput = document.getElementById('superUserPassword');

		const sessionName = sessionNameInput.value.trim();
		const password = passwordInput.value;

		// Enhanced validation
		const sessionNameRegex = /^[a-zA-Z0-9_]{3,30}$/;
		const errors = [];

		if (!sessionNameRegex.test(sessionName)) {
			errors.push('Session name must be 3-30 characters long and contain only letters, numbers, and underscores');
		}
		if (password.length < 8) {
			errors.push('Password must be at least 8 characters long');
		}
		if (!/[A-Z]/.test(password)) {
			errors.push('Password must contain at least one uppercase letter');
		}
		if (!/[a-z]/.test(password)) {
			errors.push('Password must contain at least one lowercase letter');
		}
		if (!/[0-9]/.test(password)) {
			errors.push('Password must contain at least one number');
		}

		if (errors.length > 0) {
			window.alert(t('validation.password_required_message', errors.join('\n')));
			return;
		}

		// Sanitize the session name
		const sanitizedSessionName = sessionName.replace(/[^a-zA-Z0-9_]/g, '');

		try {
			const username = `Zederal_Reserve_V${Math.floor(Math.random() * 100000)}`;

			const response = await fetch(`${apiBaseUrl}/api/session/create`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					sessionName: sanitizedSessionName,
					superUserDetails: {
						username,
						password
					}
				})
			});

			if (response.ok) {
				const data = await response.json();

				// Ensure we're using the exact same structure as the login function
				const portfolioId = data.superUserId; // This is the key alignment

				localStorage.clear(); // Clear any existing data
				localStorage.setItem('portfolioId', portfolioId);
				localStorage.setItem('token', data.token);
				localStorage.setItem('role', 'superuser');
				localStorage.setItem('sessionId', data.sessionId);

				// Wait for storage to be ready
				await ensureStorageReady(['portfolioId', 'token', 'role', 'sessionId']);

				// Show credentials modal with aligned data
				showCredentialsModal(portfolioId, password, {
					...data,
					portfolioId // Include portfolioId in the data passed to modal
				});
			} else {
				const errorData = await response.json();
				throw new Error(errorData.error || 'Failed to create session');
			}
		} catch (error) {
			console.error('Error creating session:', error);
			window.alert(t('messages.error', `Failed to create session: ${error.message}`));
		}
	});

	// Close button handler
	document.getElementById('closeModalButton').addEventListener('click', () => {
		modal.remove();
	});
}

/***********************************************
 * SHOW CREDENTIALS MODAL
 ***********************************************/
function showCredentialsModal(portfolioId, password, sessionData) {
	try {
		const createSessionModal = document.getElementById('createSessionModal');
		const credentialsModal = document.getElementById('credentialsModal');

		// Show portfolioId for login
		document.getElementById('generatedUsername').textContent = portfolioId;
		document.getElementById('generatedPassword').textContent = password;

		// Update label (if needed)
		document.querySelector('.credential-item label').textContent = 'Portfolio ID:';

		// Generate QR code data
		let qrData;
		try {
			const loginUrl = new URL(`${window.location.origin}/autologin`);
			loginUrl.searchParams.set('portfolioId', portfolioId);
			loginUrl.searchParams.set('token', sessionData.token);
			loginUrl.searchParams.set('sessionId', sessionData.sessionId);
			qrData = loginUrl.toString();
		} catch (e) {
			console.error('Error creating QR data:', e);
			qrData = portfolioId;
		}

		// Clear previous QR code if any
		const qrContainer = document.getElementById('qrCodeContainer');
		qrContainer.innerHTML = '';

		// Create QR code using qrcodejs
		try {
			new QRCode(qrContainer, {
				text: qrData,
				width: 200,
				height: 200,
				colorDark: "#000000",
				colorLight: "#ffffff",
				correctLevel: QRCode.CorrectLevel.H
			});
		} catch (e) {
			console.error('Error generating QR code:', e);
			qrContainer.textContent = 'Failed to generate QR code';
		}

		// Replace continue button with "Go to Login"
		document.getElementById('continueToSession').textContent = 'Go to Login';
		document.getElementById('continueToSession').addEventListener('click', () => {
			try {
				// Store the credentials temporarily
				sessionStorage.setItem('tempPortfolioId', portfolioId);
				sessionStorage.setItem('tempPassword', password);

				// Clear any existing localStorage data
				localStorage.clear();

				// Redirect to index.html
				window.location.href = '/index.html?prefill=true';
			} catch (error) {
				console.error('Error redirecting to login:', error);
				window.alert(t('messages.error', 'Error preparing login. Please copy your credentials and login manually.'));
			}
		});

		// Update copy buttons
		document.querySelectorAll('.copy-btn').forEach(btn => {
			btn.addEventListener('click', () => {
				const type = btn.dataset.copy;
				const text = type === 'username' ? portfolioId : password;
				navigator.clipboard.writeText(text)
					.then(() => {
						btn.textContent = 'Copied!';
						setTimeout(() => btn.textContent = 'Copy', 2000);
					})
					.catch(() => window.alert(t('messages.error', 'Failed to copy to clipboard')));
			});
		});

		createSessionModal.style.display = 'none';
		credentialsModal.style.display = 'block';
	} catch (e) {
		console.error('Error in showCredentialsModal:', e);
		window.alert(t('messages.error', 'Error showing credentials. Please try again.'));
	}
}



function generateEmpatheticMessage(mood, reason) {
  mood = mood.toLowerCase();
  if (mood.includes('sad') || mood.includes('depressed') || mood.includes('anxious')) {
	return `Sorry to hear you're feeling ${mood}. We'll try to reflect that in your simulation.`;
  } else if (mood.includes('happy') || mood.includes('excited') || mood.includes('good')) {
	return `Great to hear you're feeling ${mood}! Let's build something that matches that energy.`;
  } else {
	return `Thanks for sharing that you're feeling ${mood}. Let's see what we can generate for you.`;
  }
}

function showSimulatorQuestionModal() {
  const modal = document.createElement('div');
  modal.classList.add('simulator-modal');
  modal.style.display = 'flex';
  modal.innerHTML = `
	<div class="simulator-modal-content">
	  <h2>${t('simulation.life_profit_title','Exploring What You Value')}</h2>
	  <p class="session-note">${t('simulation.one_off_session','ZExchange Feestyle Session')}</p>

	  <!-- Q1: Core orientation -->
	  <p><strong>${t('forms.mood_one_word','How or what are you feeling are living right now?')}</strong> ${t('forms.one_word_hint','(1 word)')}</p>
	  <input id="simQ1" maxlength="20" placeholder="${t('forms.single_word_example','e.g., happy, sad, drunk, joy...')}"/>

	  <!-- Q2: Personal meaning of value -->
	  <p><strong>${t('forms.value_definition','Why do you feel this way? (Value)')}</strong></p>
	  <textarea id="simQ2" placeholder="${t('forms.value_definition_placeholder','e.g., I got a promotion, I am hung over, My kid scored 3 goals...')}"></textarea>

	  <!-- Q3: Response to value being affirmed or challenged -->
	  <p><strong>${t('forms.value_reaction','What does this feeling give you? (Life Profits)')}</strong></p>
	  <textarea id="simQ3" placeholder="${t('forms.value_reaction_placeholder','e.g., gives me energy, makes me depressed, helps me grow...')}"></textarea>

	  <button id="submitSimAnswers" class="simulator-setup-btn">${t('buttons.complete_setup_button','Complete Setup')}</button>
	</div>
  `;
  document.body.appendChild(modal);

  document.getElementById('submitSimAnswers').addEventListener('click', async () => {
	const q1 = document.getElementById('simQ1').value.trim();
	const q2 = document.getElementById('simQ2').value.trim();
	const q3 = document.getElementById('simQ3').value.trim();

	if (!q1 || !q2 || !q3 || q1.split(/\s+/).length > 1) {
	  return window.alert(t(
		'validation.simulation_questions_required',
		"Please answer all questions. Enter a single word for your guiding quality and complete both reflections."
	  ));
	}
	modal.remove();
	
	const thinkingModal = showThinkingModal();
	try {
	  const res = await fetch(`${apiBaseUrl}/api/company/generate-company-profile`, {
		method: 'POST',
		headers: { 
		  'Content-Type': 'application/json',
		  ...(localStorage.getItem("token") && {
			Authorization: `Bearer ${localStorage.getItem("token")}`
		  })
		},
		body: JSON.stringify({ 
		  responseQ1: q1, 
		  responseQ2: q2, 
		  responseQ3: q3 
		})
	  });

	  if (!res.ok) {
		const errorText = await res.text();
		throw new Error(`API Error: ${errorText}`);
	  }

	  const result = await res.json();
	  console.log("🧠 Value-based analysis result:", result);

	  const { empathy, companies, analysis } = result;

	  if (!Array.isArray(companies) || companies.length === 0) {
		thinkingModal.remove();
		showNoMatchesFoundModal(q1, q2, q3);
		return;
	  }

	  const message = empathy || generateEmpatheticMessage(q1, q2);

	  if (analysis) {
		console.log("🎯 Value-based Analysis:", {
		  themes: analysis.themes,
		  sentiment: analysis.sentiment,
		  needs: analysis.needs,
		  approach: analysis.matchingApproach
		});
	  }

	  showCompanySelectionModal(companies, message, analysis);
	  thinkingModal.remove();
	} catch (err) {
	  console.error("Simulator setup error:", err);
	  window.alert(t('messages.error', `Error generating simulator session: ${err.message}`));
	  thinkingModal.remove();
	}
  });
}

 // Only this modal appears when backend finds no matches:
 function showNoMatchesFoundModal(q1, q2, q3) {
   const modal = document.createElement('div');
   modal.classList.add('simulator-modal');
   modal.innerHTML = `
	 <div class="simulator-modal-content">
	   <h2> No Company Matches Found</h2>
	   <p>Our system couldn't find companies that match your emotional profile. This can happen with very unique or specific responses.</p>
	   
	   <div style="text-align: left; margin: 20px 0; padding: 15px; background: rgba(255,165,0,0.1); border-radius: 5px; border-left: 4px solid #ffa500;">
		 <p><strong>You can:</strong></p>
		 <ul style="margin: 10px 0; padding-left: 20px;">
		   <li>Try different emotional words or descriptions</li>
		   <li>Be more or less specific in your responses</li>
		   <li>Use a generic portfolio to get started</li>
		 </ul>
	   </div>
	   
	   <button id="retryWithDifferentAnswers" class="simulator-setup-btn">Try Different Answers</button>
	   <button id="useGenericPortfolio" class="secondary-btn" style="background: #666; margin-left: 10px;">Use Generic Portfolio</button>
	 </div>
   `;
   document.body.appendChild(modal);
   
   document.getElementById('retryWithDifferentAnswers').addEventListener('click', () => {
	 modal.remove();
	 // User can modify their answers in the still-open question modal
   });
   
   document.getElementById('useGenericPortfolio').addEventListener('click', () => {
	 modal.remove();
	 // Provide fallback companies
	 const fallbackCompanies = [
	   { name: "Wellness Solutions", output: "wellness", description: "General wellness and life improvement services" },
	   { name: "Life Balance Co", output: "balance", description: "Tools and services for maintaining life balance" },
	   { name: "Personal Growth Inc", output: "growth", description: "Personal development and self-improvement" }
	 ];
	 showCompanySelectionModal(fallbackCompanies, "Here are some general companies to get you started.", null);
   });
 }

function showCompanySelectionModal(companyOptions, message, analysis) {
  const modal = document.createElement('div');
  modal.classList.add('simulator-modal');

  const marketResponseLabel = t('simulation.market_response_label', 'Market Response');
  const selectHeading       = t('simulation.select_company_heading', 'Select a company aligned with your emotional state.');
  const outputLabel         = t('labels.output', 'Output');
  const chooseLabel         = t('buttons.choose_company', 'Choose This Company');

  // Read strictly from translations when we have a CSV Id; otherwise use server fields.
  const readStrict = (company) => {
	const rowIdRaw = company.csvRow ?? company.Id ?? company.ID ?? '';
	const rowId = rowIdRaw != null ? String(rowIdRaw).trim() : '';

	if (rowId) {
	  // Ask i18n first; if the key isn't found (returns key/undefined), fall back to server values.
	  const get = (key, fb) => {
		const v = t(key, undefined);
		return (v && v !== key) ? v : fb;
	  };

	  const name = get(`companies_by_id.${rowId}.Company_Name`,
					   company['Company Name'] || company.name || '');
	  const out  = get(`companies_by_id.${rowId}.Out_put`,
					   company['Out put'] || company['Output'] || company.output || '');
	  const desc = get(`companies_by_id.${rowId}.Description`,
					   company['Description'] || company.description || '');
	  const risk = get(`companies_by_id.${rowId}.Risk`,
					   company['Risk'] || company.risk || '');

	  return { name, out, desc, risk };
	}

	// No Id → just use server values
	return {
	  name: company.name || company['Company Name'] || t('placeholders.unnamed_company', 'Unnamed Company'),
	  out:  company.output || company['Out put'] || t('placeholders.unknown_output', 'Unknown Output'),
	  desc: company.description || company['Description'] || t('placeholders.no_description', 'No description available'),
	  risk: company.risk || company['Risk'] || ''
	};
  };

  const listHtml = companyOptions.map((company, idx) => {
	const i18n = readStrict(company);

	return `
	  <div class="company-option" data-index="${idx}">
		<h4>${i18n.name}</h4>
		<p class="indexModSimPar"><strong>${outputLabel}:</strong> ${i18n.out}</p>
		<p class="indexModSimPar">${i18n.desc}</p>
		<button class="simulator-setup-btn choose-company-btn" data-index="${idx}">${chooseLabel}</button>
		<hr />
	  </div>
	`;
  }).join('');

  modal.innerHTML = `
	<div class="simulator-modal-content">
	  <p><u>${marketResponseLabel}<br></u><br>${message || ''}</p>
	  <h2>${selectHeading}</h2>
	  ${listHtml}
	  <button class="simulator-close-btn" onclick="window.location.href='index.html'">Close</button>
	</div>
  `;
  document.body.appendChild(modal);

  // Choose company button handler (keeps Id internally; does NOT render it)
  document.querySelectorAll('.choose-company-btn').forEach(btn => {
	btn.addEventListener('click', async (e) => {
	  const idx = +e.target.getAttribute('data-index');
	  const selectedCompany = companyOptions[idx];

	  // Compute localized fields for later reuse
	  const rowIdRaw = selectedCompany.csvRow ?? selectedCompany.Id ?? selectedCompany.ID ?? '';
	  const rowId = rowIdRaw != null ? String(rowIdRaw).trim() : '';

	  const loc = rowId ? {
		name: t(`companies_by_id.${rowId}.Company_Name`, selectedCompany.name || selectedCompany['Company Name'] || ''),
		out:  t(`companies_by_id.${rowId}.Out_put`,     selectedCompany.output || selectedCompany['Out put'] || ''),
		desc: t(`companies_by_id.${rowId}.Description`, selectedCompany.description || selectedCompany['Description'] || '')
	  } : {
		name: selectedCompany.name || selectedCompany['Company Name'] || '',
		out:  selectedCompany.output || selectedCompany['Out put'] || '',
		desc: selectedCompany.description || selectedCompany['Description'] || ''
	  };

	  const enrichedSelectedCompany = {
		...selectedCompany,
		csvRow: rowId || selectedCompany.csvRow || '',   // keep Id internally for future translations
		_i18n: loc                                       // localized cache for later UI screens
	  };

	  localStorage.setItem('simulatedCompany', JSON.stringify(enrichedSelectedCompany));
	  modal.remove();
	  showPortfolioCreationLoading();

	  try {
		const preferredLanguage = localStorage.getItem('preferredLanguage') || 'en';
		
		const res = await fetch(`${apiBaseUrl}/api/sim/createSession`, {
		  method: 'POST',
		  headers: { 'Content-Type': 'application/json' },
		  body: JSON.stringify({
			simulatedCompany: selectedCompany,  
			psychologyAnalysis: analysis,
			preferredLanguage
		  })
		});

		if (!res.ok) {
		  const err = await res.json().catch(() => ({}));
		  throw new Error(err.error || 'Simulator setup failed');
		}

		const data = await res.json();
		let { portfolioId, sessionId, token, simulationEndTime, simulationTimerId, scenario, lifeProfile } = data;

		if (!simulationEndTime || !simulationTimerId) {
		  simulationEndTime = new Date(Date.now() + 10 * 60 * 1000).toISOString();
		  simulationTimerId = `local-fallback-${Date.now()}`;
		}

		localStorage.setItem('portfolioId', portfolioId);
		localStorage.setItem('sessionId', sessionId);
		localStorage.setItem('token', token);
		localStorage.setItem('role', 'user');
		localStorage.setItem('firstLogin', 'false');
		localStorage.setItem('isSimulated', 'true');
		localStorage.setItem('simulationEndTime', simulationEndTime);
		localStorage.setItem('simulationTimerId', simulationTimerId);

		// NEW: Store scenario info for access later
		if (scenario) {
		  localStorage.setItem('classScenario', JSON.stringify(scenario));
		}

		// What this life carries. Kept so the floor can speak it later too,
		// not just the entry popup.
		if (lifeProfile) {
		  localStorage.setItem('lifeProfile', JSON.stringify(lifeProfile));
		}

		if (analysis) localStorage.setItem('psychologyAnalysis', JSON.stringify(analysis));
		if (typeof initializeSimulationTimer === 'function') {
		  initializeSimulationTimer(simulationEndTime, simulationTimerId);
		}

		// NEW: Show class scenario popup before redirecting
		if (scenario) {
		  await showClassScenarioPopup(scenario, lifeProfile);
		}

		setTimeout(() => { window.location.href = 'zfloor.html'; }, 1200);
	  } catch (err) {
		console.error('Simulator setup error:', err);
		window.alert(t('messages.error', `Error generating simulator session: ${err.message}`));
	  }
	});
  });
}

// NEW: Class Scenario Popup
// `lifeProfile` is what the participant's own three answers turned into: the
// company they chose, what carrying it costs them (`heavy`) and what lives
// unlike theirs have that theirs doesn't (`lacking`). See backend/lifeProfile.js.
//
// The class block above it is what the world imposed. This is theirs — so it
// is shown second, in their own words, in the same breath.
function buildLifeCarriesBlock(lifeProfile) {
  if (!lifeProfile) return '';
  const heavy = (lifeProfile.heavy || []).slice(0, 3);
  const lacking = (lifeProfile.lacking || []).slice(0, 2);
  if (!heavy.length && !lacking.length) return '';

  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // Company names are translated per CSV row id — the same lookup used when the
  // session is created. Without it a French session reads "Vous avez choisi
  // Z Gossip Telecom" instead of "Z Ragots Télécom". EN has no companies_by_id
  // block, so the stored name is the fallback and English is unaffected.
  const rowId = lifeProfile.companyCsvId ? String(lifeProfile.companyCsvId).trim() : '';
  const stored = lifeProfile.companyName || '';
  const company = rowId ? t(`companies_by_id.${rowId}.Company_Name`, stored) : stored;

  const chose = company
    ? t('life_profile.chose', 'You chose {company}. That is what your life is heavy with.')
        .replace('{company}', esc(company))
    : '';

  // The baseline vocabulary in ZEconomy_baseline.csv is English, so each term
  // is looked up in the FR life_terms glossary. EN has no such block, so the
  // term itself is the fallback and English output is unchanged.
  const term = w => esc(t(`life_terms.${w}`, w));

  const line = (label, terms) => terms.length ? `
	  <p style="margin: 8px 0 0 0; font-size: clamp(0.9em, 3vw, 1em);">
		<strong style="color: #fcba03;">${esc(label)}</strong>
		<span style="color: #fff;">${terms.map(term).join(' &middot; ')}</span>
	  </p>` : '';

  return `
	  <div style="margin: 20px 0; padding: 15px; background: rgba(0, 255, 0, 0.06); border-left: 4px solid #00ff00; text-align: left;">
		<p style="margin: 0 0 10px 0; color: #00ff00; font-size: clamp(0.95em, 3vw, 1.1em); text-transform: uppercase; letter-spacing: 1px;">
		  ${esc(t('life_profile.carries_title', 'What your life carries'))}
		</p>
		${chose ? `<p style="margin: 0; font-size: clamp(0.95em, 3vw, 1.05em); color: #fff; line-height: 1.5;">${chose}</p>` : ''}
		${line(t('life_profile.heavy_label', 'Too much'), heavy)}
		${line(t('life_profile.lacking_label', 'None at all'), lacking)}
		<p style="margin: 14px 0 0 0; font-size: clamp(0.85em, 2.8vw, 0.95em); color: #fcba03; line-height: 1.5;">
		  ${esc(t('life_profile.how_it_ends', 'When time runs out, the room decides your class by how balanced you are and how much you gave — not by what you earned.'))}
		</p>
	  </div>`;
}

function showClassScenarioPopup(scenario, lifeProfile = null) {
  return new Promise((resolve) => {
	const popup = document.createElement('div');
	popup.style.cssText = `
	  position: fixed;
	  top: 50%;
	  left: 50%;
	  transform: translate(-50%, -50%);
	  background: linear-gradient(135deg, rgba(0, 0, 0, 0.95), rgba(20, 20, 40, 0.95));
	  border: 3px solid #00ff00;
	  padding: 40px;
	  z-index: 10000;
	  max-width: 600px;
	  max-height: 90vh;
	  overflow-y: auto;
	  overflow-x: hidden;
	  -webkit-overflow-scrolling: touch;
	  width: 90vw;
	  font-family: 'bithigh', Arial, sans-serif;
	  color: #00ff00;
	  box-shadow: 0 0 30px rgba(0, 255, 0, 0.3);
	  text-align: center;
	`;

	// Translate class name using social_class translation keys
	const classId = scenario.id || scenario.classId;
	console.log('🔍 [Class Popup] scenario object:', scenario);
	console.log('🔍 [Class Popup] classId:', classId);
	console.log('🔍 [Class Popup] Translation key will be:', `social_class.${classId}.name`);

	const translatedClassName = classId ? t(`social_class.${classId}.name`, scenario.className) : scenario.className;
	const translatedDescription = classId ? t(`social_class.${classId}.description`, scenario.description) : scenario.description;
	// A goalId means one of the class's explicit goals was randomly assigned —
	// translate that specific goal; fall back to the class's generic goal line.
	const translatedGoal = (classId && scenario.goalId)
	  ? t(`social_class.${classId}.goals.${scenario.goalId}`, scenario.goal)
	  : classId ? t(`social_class.${classId}.goal`, scenario.goal) : scenario.goal;

	console.log('🔍 [Class Popup] translatedClassName:', translatedClassName);
	console.log('🔍 [Class Popup] translatedDescription:', translatedDescription);
	console.log('🔍 [Class Popup] translatedGoal:', translatedGoal);

	popup.innerHTML = `
	  <h1 style="color: #00ff00; margin-top: 0; text-transform: uppercase; font-size: clamp(1.3em, 5vw, 2em);">
		${translatedClassName}
	  </h1>

	  <div style="margin: 20px 0; padding: 15px; background: rgba(0, 255, 0, 0.1); border: 1px solid #00ff00;">
		<p style="font-size: clamp(1em, 3.5vw, 1.2em); line-height: 1.6; margin: 0;">
		  ${translatedDescription}
		</p>
	  </div>

	  <div style="margin: 20px 0; text-align: left;">
		<p style="margin: 10px 0; font-size: clamp(0.9em, 3vw, 1em);">
		  <strong style="color: #fcba03;">${t('social_class.starting_capital', 'Starting Capital:')}</strong>
		  <span style="color: ${scenario.startingPoints >= 0 ? '#00ff00' : '#ff0000'};">
			${scenario.startingPoints}
		  </span>
		</p>
		<p style="margin: 10px 0; font-size: clamp(0.9em, 3vw, 1em);">
		  <strong style="color: #fcba03;">${t('social_class.starting_items', 'Starting Items:')}</strong>
		  <span style="color: #00ff00;">${scenario.startingItems}</span>
		</p>
	  </div>

	  ${buildLifeCarriesBlock(lifeProfile)}

	  <div style="margin: 20px 0; padding: 15px; background: rgba(252, 186, 3, 0.1); border-left: 4px solid #fcba03;">
		<p style="margin: 0; color: #fcba03; font-size: clamp(0.95em, 3vw, 1.1em);">
		  <strong>${t('social_class.your_goal', 'Your Goal:')}</strong>
		</p>
		<p style="margin: 10px 0 0 0; font-size: clamp(0.95em, 3vw, 1.1em); color: #fff;">
		  ${translatedGoal}
		</p>
	  </div>

	  <p style="font-size: clamp(0.8em, 2.5vw, 0.9em); color: #888; margin: 20px 0;">
		${t('social_class.actions_note', 'Your actions shape how others treat you. Generosity builds alliances, exploitation invites retaliation.')}
	  </p>

	  <button id="closeScenarioPopup" style="
		margin-top: 20px;
		padding: clamp(12px, 3vw, 15px) clamp(30px, 8vw, 40px);
		background: #00ff00;
		color: #000;
		border: none;
		cursor: pointer;
		font-family: 'bithigh', Arial, sans-serif;
		font-size: clamp(1em, 3.5vw, 1.2em);
		text-transform: uppercase;
		transition: all 0.3s;
		width: auto;
		min-width: 200px;
	  " onmouseover="this.style.background='#00cc00'; this.style.transform='scale(1.05)';"
		 onmouseout="this.style.background='#00ff00'; this.style.transform='scale(1)';">
		Begin Simulation
	  </button>
	`;

	document.body.appendChild(popup);

	document.getElementById('closeScenarioPopup').onclick = () => {
	  popup.remove();
	  resolve();
	};
  });
}

function showThinkingModal() {
  const modal = document.createElement('div');
  modal.classList.add('simulator-modal');
  modal.innerHTML = `
	<div class="simulator-modal-content">
	  <h2>${t('messages.loading','Loading...')}</h2>
	  <p>${t('simulation.please_wait','Please wait while we generate your virtual portfolio.')}</p>
	</div>
  `;
   document.body.appendChild(modal);
  return modal;
}

function showPortfolioCreationLoading() {
  const modal = document.createElement('div');
  modal.classList.add('simulator-modal');
  modal.innerHTML = `
	<div class="simulator-modal-content">
	  <h2>${t('simulation.creating_simulation','Creating your Simulation...')}</h2>
	  <p>${t('simulation.please_wait','Please wait while we generate your virtual portfolio.')}</p>
	</div>
  `;
  document.body.appendChild(modal);
}

document.addEventListener("DOMContentLoaded", () => {
   console.log('DOM Content Loaded. Checking localStorage:', {
	 firstLogin: localStorage.getItem('firstLogin'),
	 portfolioId: localStorage.getItem('portfolioId'),
	 role: localStorage.getItem('role')
   });
 
  
   const simLink = document.getElementById('launchSimulator');
   if (simLink) {
	 // Remove any existing listeners by cloning
	 const newSimLink = simLink.cloneNode(true);
	 simLink.parentNode.replaceChild(newSimLink, simLink);
	 
	newSimLink.addEventListener('click', (e) => {
	  e.preventDefault();
	  console.log('Simulator clicked');
	  try {
		showSimulatorChoiceModal();
		console.log('showSimulatorChoiceModal called successfully');
	  } catch (error) {
		console.error('Error calling showSimulatorChoiceModal:', error);
	  }
	});
   }
// Lazy loading banner system with Intersection Observer
   const bannerLoadStates = new Map();

   function createOptimizedBannerLoader(videoElement, bannerUrl, bannerName) {
	 return new Promise((resolve) => {
	   if (bannerLoadStates.get(bannerName)) {
		 resolve();
		 return;
	   }

	   const bannerContainer = videoElement.parentElement;

	   if (Hls.isSupported()) {
		 const hls = new Hls(optimizedHlsConfig);
		 hls.loadSource(bannerUrl);
		 hls.attachMedia(videoElement);

		 hls.on(Hls.Events.MANIFEST_PARSED, () => {
		   videoElement.play().catch(err => console.warn(`${bannerName} play failed:`, err));
		   bannerLoadStates.set(bannerName, true);
		   if (bannerContainer) bannerContainer.classList.add('banner-loaded');
		   resolve();
		 });

		 hls.on(Hls.Events.ERROR, (event, data) => {
		   if (data.fatal) {
			 console.warn(`${bannerName} HLS error:`, data.type);
			 bannerLoadStates.set(bannerName, 'error');
		   }
		 });

		 // Store hls instance for cleanup if needed
		 videoElement._hlsInstance = hls;
	   } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
		 videoElement.src = bannerUrl;
		 videoElement.addEventListener('loadedmetadata', () => {
		   videoElement.play().catch(err => console.warn(`${bannerName} play failed:`, err));
		   bannerLoadStates.set(bannerName, true);
		   if (bannerContainer) bannerContainer.classList.add('banner-loaded');
		   resolve();
		 }, { once: true });
	   }
	 });
   }

   // Intersection Observer for lazy loading banners
   const bannerObserver = new IntersectionObserver((entries) => {
	 entries.forEach(entry => {
	   if (entry.isIntersecting) {
		 const videoElement = entry.target.querySelector('video');
		 const bannerName = videoElement?.id;

		 if (videoElement && !bannerLoadStates.get(bannerName)) {
		   const bannerUrl = videoElement.dataset.bannerUrl;
		   if (bannerUrl) {
			 createOptimizedBannerLoader(videoElement, bannerUrl, bannerName);
			 bannerObserver.unobserve(entry.target);
		   }
		 }
	   }
	 });
   }, {
	 root: null,
	 rootMargin: '50px', // Start loading 50px before entering viewport
	 threshold: 0.1
   });

   // Simulator banner setup
   const simulatorBannerVideo = document.getElementById('SimulatorBanner');
   const simulatorBanner = document.getElementById('simulator-banner');

   if (simulatorBannerVideo && simulatorBanner) {
	 simulatorBannerVideo.dataset.bannerUrl = 'uploads/banners/index/1/playlist.m3u8';

	 // Delay simulator banner load to prioritize login banner
	 setTimeout(() => {
	   bannerObserver.observe(simulatorBanner);
	 }, 800);

	 // Click handler
	 simulatorBanner.addEventListener('click', (e) => {
	   e.preventDefault();
	   console.log('Simulator banner clicked');
	   try {
		 showSimulatorChoiceModal();
	   } catch (error) {
		 console.error('Error calling showSimulatorChoiceModal:', error);
	   }
	 });
   }

   // Programs banner setup
   const programsBannerVideo = document.getElementById('ProgramsBanner');
   const programsBanner = document.getElementById('programs-banner');

   if (programsBannerVideo && programsBanner) {
	 programsBannerVideo.dataset.bannerUrl = 'uploads/banners/index/5/playlist.m3u8';

	 // Loop handler for Safari
	 programsBannerVideo.addEventListener('ended', () => {
	   programsBannerVideo.currentTime = 0;
	   programsBannerVideo.play().catch(err => console.error('Programs banner loop error:', err));
	 });

	 // Delay programs banner load
	 setTimeout(() => {
	   bannerObserver.observe(programsBanner);
	 }, 1200);
   }

   // Corporate banner setup
   const corporateBannerVideo = document.getElementById('CorporateBanner');
   const corporateBanner = document.getElementById('corporate-banner');

   if (corporateBannerVideo && corporateBanner) {
	 corporateBannerVideo.dataset.bannerUrl = 'uploads/banners/index/6/playlist.m3u8';

	 // Loop handler for Safari
	 corporateBannerVideo.addEventListener('ended', () => {
	   corporateBannerVideo.currentTime = 0;
	   corporateBannerVideo.play().catch(err => console.error('Corporate banner loop error:', err));
	 });

	 // Delay corporate banner load
	 setTimeout(() => {
	   bannerObserver.observe(corporateBanner);
	 }, 1600);
   }


	const firstLoginStatus = localStorage.getItem('firstLoginStatus'); // 'pending' | 'done' | null
	const portfolioId = localStorage.getItem('portfolioId');
	const role = localStorage.getItem('role');
	const token = localStorage.getItem('token');
	
	console.log('First login state:', { firstLoginStatus, portfolioId, role: role || null });
	
	(async () => {
	  // If first-login is in progress and we still have a token, resume the modal
	  if (firstLoginStatus === 'pending' && token && portfolioId) {
		try {
		  await ensureStorageReady(['portfolioId', 'token']);
		  showFirstLoginModal(portfolioId);
		  return; // Do not redirect
		} catch {
		  // If storage looks broken, clear ONLY the state flag to avoid loops
		  localStorage.removeItem('firstLoginStatus');
		}
	  }
	
	  // If somehow we have 'pending' but no token, clean up the stale flag
	  if (firstLoginStatus === 'pending' && !token) {
		localStorage.removeItem('firstLoginStatus');
	  }
	
	  // Role-based redirection for already authenticated users
	  if (role === 'broker') {
		window.location.href = '/order.html';
		return;
	  } else if (role === 'superuser') {
		window.location.href = '/boss.html';
		return;
	  } else if (role === 'user') {
		window.location.href = '/zfloor.html';
		return;
	  }
	
	  // else: stay on login screen
	})();

	// QR scanner logic (unchanged)
	let qrScanner;
	const startScanBtn = document.getElementById("start-scan-btn");
	const stopScanBtn = document.getElementById("stop-scan-btn");
	const qrScannerContainer = document.getElementById("qr-scanner-container");

	async function onScanSuccess(decodedText) {
	  try {
		const url = new URL(decodedText);
		const tokenFromQR = url.searchParams.get("token");
	
		if (!tokenFromQR) {
		  window.alert(t('messages.qr_error_message', 'Invalid QR code. Please try again.'));
		  return;
		}
	
		const response = await fetch(
		  `${apiBaseUrl}/api/autologin?token=${encodeURIComponent(tokenFromQR)}`,
		  {
			method: "GET",
			credentials: "include", // parity with manual login flow
			headers: { "Accept": "application/json" }
		  }
		);
	
		if (!response.ok) {
		  throw new Error(`Login failed (${response.status})`);
		}
	
		const data = await response.json();
	
		// Persist core auth
		localStorage.setItem('portfolioId', data.portfolioId);
		localStorage.setItem('token', data.token);
		localStorage.setItem('role', data.role);
	
		if (data.sessionId) {
		  localStorage.setItem('sessionId', data.sessionId);
		} else {
		  localStorage.removeItem('sessionId');
		}
	
		// Decide if we must show first-login modal (all logic stays inside this function)
		const needsFirstLogin = Boolean(
		  data?.firstLogin === true ||
		  data?.requiresSetup === true ||
		  data?.setupComplete === false ||
		  data?.hasCompletedFirstLogin === false ||
		  // heuristics if server doesn't provide explicit flags:
		  !(data?.userName || data?.username || data?.name) ||
		  !(data?.companyId || data?.systemCompanyId || data?.csvId)
		);
	
		if (needsFirstLogin) {
		  localStorage.setItem('firstLoginStatus', 'pending');
		  await ensureStorageReady(['portfolioId', 'token']);
		  showFirstLoginModal(data.portfolioId);
		  return; // don't redirect; user must complete setup
		}
	
		// Normal redirects when setup is already complete
		if (data.role === 'broker') {
		  window.location.href = '/order.html';
		} else if (data.role === 'superuser') {
		  window.location.href = '/boss.html';
		} else {
		  window.location.href = '/zfloor.html';
		}
	  } catch (error) {
		window.alert(t('auth.login_error_message', `Error during login: ${error.message}`));
	  }
	}


	function initializeScanner() {
		if (!qrScanner) {
			qrScanner = new Html5Qrcode("qr-reader");
		}

		qrScanner
			.start(
				{ facingMode: "environment" }, // Rear camera
				{ fps: 10, qrbox: { width: 250, height: 250 } },
				onScanSuccess
			)
			.catch((err) => {
				window.alert(t('messages.error', `Error initializing scanner: ${err}`));
			});
	}

	if (startScanBtn) {
		startScanBtn.addEventListener("click", () => {
			qrScannerContainer.style.display = "block";
			initializeScanner();
		});
	}
	if (stopScanBtn) {
		stopScanBtn.addEventListener("click", () => {
			if (qrScanner) {
				qrScanner.stop()
					.then(() => {
						qrScannerContainer.style.display = "none";
					})
					.catch((err) => {
						window.alert(t('messages.error', `Error stopping scanner: ${err}`));
					});
			}
		});
	}

	// Check if we should pre-fill the login form
	const urlParams = new URLSearchParams(window.location.search);
	if (urlParams.get('prefill') === 'true') {
		const prefillPortfolioId = sessionStorage.getItem('tempPortfolioId');
		const prefillPassword = sessionStorage.getItem('tempPassword');

		if (prefillPortfolioId && prefillPassword) {
			// Find and fill the login form inputs
			const portfolioIdInput = document.getElementById('portfolioId');
			const passwordInput = document.getElementById('password');

			if (portfolioIdInput && passwordInput) {
				portfolioIdInput.value = prefillPortfolioId;
				passwordInput.value = prefillPassword;

				// Clear the temporary storage
				sessionStorage.removeItem('tempPortfolioId');
				sessionStorage.removeItem('tempPassword');
			}
		}
	}

	// Handle QR code redirect - show proper intro modal flow
	const startSimulator = urlParams.get('startSimulator');
	const passCode = urlParams.get('passCode');
	if (startSimulator && passCode) {
		// Clear URL parameters to prevent re-triggering on refresh
		window.history.replaceState({}, document.title, window.location.pathname);

		// Trigger the appropriate simulator flow with intro modal
		setTimeout(async () => {
			try {
				if (startSimulator === 'freestyle') {
					await showFreestyleGate();
					showSimulatorQuestionModal();
				} else if (startSimulator === 'memento_mori') {
					await showMementoGate();
					showMementoMoriModal();
				} else if (startSimulator === 'story' || ['scifi', 'romance', 'mystery', 'fantasy', 'horror'].includes(startSimulator)) {
					// Story simulation - shows Catena intro modal first, then genre selection
					showStorySimulationModal();
				}
			} catch (error) {
				// User clicked close on intro modal
				console.log('User cancelled at intro modal');
			}
		}, 500);
	}
});

/***********************************************
 * SANITIZE INPUT (HELPER)
 ***********************************************/
function sanitizeInput(input) {
	const element = document.createElement('div');
	element.innerText = input;
	return element.innerHTML;
}

/***********************************************
 * UPDATE ACTIVE STATUS (OPTIONAL)
 ***********************************************/
async function updateActiveStatus(portfolioId) {
	try {
		const response = await fetch(`${apiBaseUrl}/api/portfolios/${portfolioId}`, {
			method: 'PATCH',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${localStorage.getItem('token')}`
			},
			body: JSON.stringify({ active: true })
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(`Failed to update active status: ${errorData.error || response.statusText}`);
		}
		console.log('Active status updated successfully');
	} catch (error) {
		console.error('Error updating active status:', error);
		throw error; // Re-throw if you want to handle it upstream
	}
}

if (typeof window !== 'undefined') {
  window.login = login; // make sure `login` is defined above
  window.openCreateSessionModal = openCreateSessionModal; // <-- critical line
  window.showSimulatorChoiceModal = window.showSimulatorChoiceModal || showSimulatorChoiceModal;
  window.showSimulatorKeyModal = showSimulatorKeyModal; // <-- expose key validation modal
}